import React, { useState, useEffect, useCallback } from 'react';
import { meetings as meetingsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];

function formatHour(h) {
  if (h === 12) return '12 PM';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
}

function normalizeMonth(raw) {
  // PostgreSQL may return full ISO string or just YYYY-MM-DD; normalize to YYYY-MM-DD
  return String(raw).slice(0, 10);
}

function getMonthDates(monthStr) {
  const d = new Date(normalizeMonth(monthStr) + 'T00:00:00');
  const year = d.getFullYear();
  const month = d.getMonth();
  const dates = [];
  const current = new Date(year, month, 1);
  while (current.getMonth() === month) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function slotKey(date, hour) {
  const d = new Date(date);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

export default function MeetingSchedulerPage() {
  const { user, isAdmin } = useAuth();
  const [poll, setPoll] = useState(null);
  const [availabilities, setAvailabilities] = useState([]);
  const [mySlots, setMySlots] = useState(new Set());
  const [dragging, setDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null); // 'add' or 'remove'
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [newMonth, setNewMonth] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [chosenDatetime, setChosenDatetime] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await meetingsApi.getActive();
      setPoll(data.poll);
      setAvailabilities(data.availabilities || []);
      if (data.poll && data.availabilities) {
        const mine = new Set(
          data.availabilities
            .filter((a) => a.user_id === user.id)
            .map((a) => a.slot)
        );
        setMySlots(mine);
      }
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = async () => {
    try {
      const data = await meetingsApi.getHistory();
      setHistory(data);
      setShowHistory(true);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const handleDragStart = (key) => {
    const mode = mySlots.has(key) ? 'remove' : 'add';
    setDragging(true);
    setDragMode(mode);
    setMySlots((prev) => {
      const next = new Set(prev);
      if (mode === 'remove') next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDragEnter = (key) => {
    if (!dragging) return;
    setMySlots((prev) => {
      const next = new Set(prev);
      if (dragMode === 'remove') next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDragEnd = () => {
    setDragging(false);
    setDragMode(null);
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      await meetingsApi.saveAvailability([...mySlots]);
      setMessage('Availability saved!');
      load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newMonth) return;
    try {
      await meetingsApi.create(newMonth + '-01');
      setMessage('Poll created!');
      setNewMonth('');
      load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  const handleConclude = async () => {
    try {
      await meetingsApi.conclude(chosenDatetime || null);
      setMessage('Poll concluded. Next month\'s poll created automatically.');
      setChosenDatetime('');
      load();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
  };

  // Build slot counts from all availabilities
  const slotCounts = {};
  const slotUsers = {};
  availabilities.forEach((a) => {
    slotCounts[a.slot] = (slotCounts[a.slot] || 0) + 1;
    if (!slotUsers[a.slot]) slotUsers[a.slot] = [];
    slotUsers[a.slot].push(a.display_name);
  });

  const maxCount = Math.max(1, ...Object.values(slotCounts));

  if (loading) return <div className="loading">Loading...</div>;

  const monthDates = poll ? getMonthDates(poll.month) : [];
  const monthLabel = poll
    ? new Date(normalizeMonth(poll.month) + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="meeting-page">
      <h1>Meeting Scheduler</h1>
      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}<button onClick={() => setMessage(null)}>✕</button>
        </div>
      )}

      {!poll && (
        <div className="meeting-empty">
          <p>No active meeting poll.</p>
          {isAdmin && (
            <div className="meeting-create">
              <label>
                Start a poll for:
                <input type="month" value={newMonth} onChange={(e) => setNewMonth(e.target.value)} />
              </label>
              <button className="btn-sm btn-primary" onClick={handleCreate}>Create Poll</button>
            </div>
          )}
        </div>
      )}

      {poll && (
        <>
          <p className="meeting-subtitle">
            Select your available times for the <strong>{monthLabel}</strong> volunteer meeting.
            Click cells to toggle, then save.
          </p>

          <div className="meeting-grid-wrapper" onMouseUp={handleDragEnd} onMouseLeave={handleDragEnd}>
            <table className="meeting-grid" style={{ userSelect: 'none' }}>
              <thead>
                <tr>
                  <th></th>
                  {monthDates.map((d) => (
                    <th key={d.toISOString()} className="meeting-date-header">
                      <span className="meeting-weekday">{WEEKDAYS[d.getDay()]}</span>
                      <span className="meeting-day">{d.getDate()}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {HOURS.map((hour) => (
                  <tr key={hour}>
                    <td className="meeting-hour-label">{formatHour(hour)}</td>
                    {monthDates.map((d) => {
                      const key = slotKey(d, hour);
                      const isMine = mySlots.has(key);
                      const count = slotCounts[key] || 0;
                      const heatLevel = count > 0 ? Math.ceil((count / maxCount) * 4) : 0;
                      return (
                        <td
                          key={key}
                          className={`meeting-cell ${isMine ? 'mine' : ''} heat-${heatLevel}`}
                          onMouseDown={(e) => { e.preventDefault(); handleDragStart(key); }}
                          onMouseEnter={() => handleDragEnter(key)}
                          title={slotUsers[key] ? slotUsers[key].join(', ') : 'No responses yet'}
                        >
                          {count > 0 && <span className="meeting-count">{count}</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="meeting-actions">
            <button className="btn-sm btn-primary" onClick={saveAvailability} disabled={saving}>
              {saving ? 'Saving...' : 'Save My Availability'}
            </button>

            <div className="meeting-legend">
              <span className="legend-item"><span className="legend-swatch mine"></span> Your selection</span>
              <span className="legend-item"><span className="legend-swatch heat-1"></span> Low</span>
              <span className="legend-item"><span className="legend-swatch heat-4"></span> High</span>
            </div>
          </div>

          {isAdmin && (
            <div className="meeting-admin">
              <h3>Admin: Conclude Poll</h3>
              <p>Pick the final meeting time, then conclude this poll. A new poll for the following month will be created automatically.</p>
              <div className="meeting-conclude-form">
                <input
                  type="datetime-local"
                  value={chosenDatetime}
                  onChange={(e) => setChosenDatetime(e.target.value)}
                />
                <button className="btn-sm btn-primary" onClick={handleConclude}>
                  Conclude Poll
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <div className="meeting-history-toggle">
        <button className="btn-sm" onClick={loadHistory}>
          {showHistory ? 'Refresh History' : 'Show Past Meetings'}
        </button>
      </div>

      {showHistory && history.length > 0 && (
        <div className="meeting-history">
          <h3>Past Meetings</h3>
          <table className="meeting-history-table">
            <thead>
              <tr><th>Month</th><th>Chosen Time</th><th>Concluded</th></tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td>{new Date(normalizeMonth(h.month) + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</td>
                  <td>{h.chosen_datetime ? new Date(h.chosen_datetime).toLocaleString() : 'Not set'}</td>
                  <td>{h.concluded_at ? new Date(h.concluded_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
